import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Container, Card, Col, Row, CardBody } from "reactstrap";
import { Helmet } from "react-helmet-async";
import signInImage from "../../../assets/images/auth/Webvi2.jpeg";
import signUpImage from "../../../assets/images/auth/Webvi1.jpeg";
import SignUpForm from "./SignUpForm";
import WebsiteLogo from "../../../helpers/WebsiteLogo";
import VerifyEmailAndMobile from "./VerifyEmailAndMobile";
import { Swiper, SwiperSlide } from "swiper/react";
import { useSelector } from "react-redux";
// 1. Reactstrap imports (for layout and basic components)
// 2. React Bootstrap imports (specifically for the Tooltip functionality)
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

const SignUp = () => {
  const location = useLocation();

  // Helper functions to accurately parse URL parameters on initial mount and URL updates
  const getRoleFromUrl = (search) => {
    const params = new URLSearchParams(search);
    const roleParam = params.get("role");
    return roleParam === "1" ? "employer" : "candidate";
  };

  const getTabFromUrl = (search) => {
    const params = new URLSearchParams(search);
    const roleParam = params.get("role");
    return roleParam === "1" ? 1 : 2;
  };

  // Lazy initialization ensures state receives the correct URL parameter before initial render
  const [tab, setTab] = useState(() => getTabFromUrl(location.search));
  const [formData, setFormData] = useState(() => ({
    username: "",
    email: "",
    phoneNumber: "",
    password: "",
    retypePassword: "",
    role: getRoleFromUrl(location.search),
  }));

  const [showEmailVerify, setShowVerifyEmail] = useState(false);

  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    const currentRole = currentParams.get("role");

    if (currentRole !== null) {
      const selectedTab = parseInt(currentRole);
      const roleString = selectedTab === 1 ? "employer" : "candidate";

      setTab(selectedTab === 1 ? 1 : 2);
      setFormData((prev) => ({
        ...prev,
        role: roleString,
      }));
    }
  }, [location]);

  const featuredCompanies = useSelector(
    (state) => state.banners["trusted_by_companies"],
  );

  const safeCompanies = Array.isArray(featuredCompanies)
    ? featuredCompanies
    : featuredCompanies?.data || [];

  const renderTooltip = (props, item) => (
    <Tooltip id="button-tooltip" {...props}>
      {item}
    </Tooltip>
  );

  const changeTab = (tabValue) => {
    const roleString = tabValue === 1 ? "employer" : "candidate";
    setTab(tabValue);
    setFormData((prev) => ({
      ...prev,
      role: roleString,
    }));
  };

  return (
    <React.Fragment>
      <div className="main-content">
        <div className="page-content">
          <Helmet>
            <title>Sign Up | Webvi </title>
          </Helmet>
          <section className="bg-auth">
            <Container>
              <Row className="justify-content-center">
                <Col lg={12}>
                  <Card className="auth-box overflow-hidden border-0 shadow-lg">
                    <Row className="g-0">
                      {/* Image Column: order-2 makes it go to bottom on mobile */}
                      <Col
                        lg={6}
                        className="text-center order-2 order-lg-1 bg-light"
                      >
                        <CardBody className="p-4 d-flex flex-column justify-content-center h-100">
                          <Link to="/">
                            <WebsiteLogo height="55" />
                          </Link>
                          <div className="mt-5">
                            <img
                              src={tab === 2 ? signUpImage : signInImage}
                              alt="User Type"
                              className="img-fluid"
                              style={{ maxHeight: "300px" }}
                            />
                          </div>
                          <div className="py-4 trusted-sec">
                            <div className="section-title text-center">
                              <h5
                                className="title text-center mt-3 mb-4"
                                style={{ fontSize: "1rem" }}
                              >
                                Trusted by Leading Hotels & Iconic Restaurants
                              </h5>
                            </div>
                            <div className="client-swiper-wrapper trusted-company">
                              <Swiper
                                loop={true}
                                slidesPerView={4}
                                autoplay={{ delay: 1500 }}
                              >
                                {safeCompanies.map((item, key) => (
                                  <SwiperSlide key={"featured-" + key}>
                                    <div className="p-2">
                                      <img
                                        src={item.imageUrl}
                                        alt={item.name}
                                        className="img-fluid"
                                      />
                                    </div>
                                  </SwiperSlide>
                                ))}
                              </Swiper>
                            </div>
                          </div>
                        </CardBody>
                      </Col>

                      {/* Form Column: order-1 makes it appear first on mobile */}
                      <Col lg={6} className="order-1 order-lg-2">
                        <CardBody
                          className="auth-content p-4 p-lg-5 text-white h-100"
                          style={{
                            background:
                              "linear-gradient(135deg, #387ed8 0%, #2a5eb2 100%)",
                          }}
                        >
                          {showEmailVerify ? (
                            <VerifyEmailAndMobile formData={formData} />
                          ) : (
                            <div className="w-100">
                              <div className="text-center mb-4 pb-2">
                                <h3 className="fw-extrabold text-white tracking-tight mb-2">
                                  Let's Get Started
                                </h3>

                                {/* Employer Offer Banner (tab === 1 / role === "employer") */}
                                {tab === 1 ? (
                                  <div className="d-inline-flex align-items-center gap-2 bg-white bg-opacity-15 border border-white border-opacity-25 rounded-pill px-3 py-1.5 my-2 shadow-sm">
                                    <span className="badge bg-warning text-dark fw-bold rounded-pill text-uppercase fs-11">
                                      Free Offer
                                    </span>
                                    <span className="fw-bold text-primary fs-13 mb-0">
                                      Get Free Job Post on your first sign up!
                                    </span>
                                  </div>
                                ) : (
                                  /* Candidate Offer Banner (tab === 2 / role === "candidate") */
                                  <div className="d-inline-flex align-items-center gap-2 bg-white bg-opacity-15 border border-white border-opacity-25 rounded-pill px-3 py-1.5 my-2 shadow-sm">
                                    <span className="badge bg-warning text-dark fw-bold rounded-pill text-uppercase fs-11">
                                      100% Free
                                    </span>
                                    <span className="fw-bold text-primary fs-13 mb-0">
                                      Apply directly to top hiring hotels &amp;
                                      restaurants!
                                    </span>
                                  </div>
                                )}

                                <p className="text-primary-50 opacity-75 fs-6 fw-normal mt-2">
                                  Create your account to continue
                                </p>
                              </div>

                              <SignUpForm
                                formData={formData}
                                setFormData={setFormData}
                                tab={tab}
                                changeTab={changeTab}
                                setShowVerifyEmail={setShowVerifyEmail}
                              />

                              <div className="mt-4 text-center">
                                <p className="mb-0 text-white-50">
                                  Already a member?{" "}
                                  <Link
                                    to="/signin"
                                    className="text-white fw-bold text-decoration-underline"
                                  >
                                    Sign In
                                  </Link>
                                </p>
                              </div>
                            </div>
                          )}
                        </CardBody>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            </Container>
          </section>
        </div>
      </div>
    </React.Fragment>
  );
};

export default SignUp;
